import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Edit, Trash2, Car, Loader2, Wrench, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const STATUT_LABELS = { disponible: "Disponible", en_pret: "En prêt", maintenance: "Maintenance" };
const STATUT_COLORS = {
  disponible: "bg-[#52a675]/15 text-[#3d8a5c] border-[#52a675]/40",
  en_pret: "bg-blue-50 text-blue-700 border-blue-200",
  maintenance: "bg-orange-50 text-orange-700 border-orange-200",
};

function FlotteDialog({ open, onClose, onSaved, editing }) {
  const [form, setForm] = useState({
    marque: "", modele: "", plaque: "", annee: "", vin: "", carburant: "",
    couleur: "", km_reference: "", tarif_jour: "", notes: "", actif: true,
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      if (editing) {
        setForm({
          marque: editing.marque || "", modele: editing.modele || "",
          plaque: editing.plaque || "", annee: editing.annee || "",
          vin: editing.vin || "", carburant: editing.carburant || "",
          couleur: editing.couleur || "",
          km_reference: editing.km_reference ?? "",
          tarif_jour: editing.tarif_jour ?? "",
          notes: editing.notes || "",
          actif: editing.actif !== false,
        });
      } else {
        setForm({
          marque: "", modele: "", plaque: "", annee: "", vin: "", carburant: "",
          couleur: "", km_reference: "0", tarif_jour: "45", notes: "", actif: true,
        });
      }
    }
  }, [open, editing]);

  const submit = async () => {
    if (!form.marque || !form.modele || !form.plaque) {
      toast.error("Marque, modèle et plaque sont obligatoires");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        marque: form.marque.trim(),
        modele: form.modele.trim(),
        plaque: form.plaque.trim().toUpperCase(),
        annee: form.annee ? parseInt(form.annee, 10) : null,
        vin: form.vin.trim().toUpperCase() || null,
        carburant: form.carburant || null,
        couleur: form.couleur.trim() || null,
        km_reference: parseInt(form.km_reference || 0, 10),
        tarif_jour: parseFloat(form.tarif_jour || 0),
        notes: form.notes || null,
        actif: form.actif,
      };
      if (editing) {
        await api.put(`/flotte/${editing.id}`, payload);
        toast.success(`${payload.marque} ${payload.modele} mis à jour`);
      } else {
        await api.post("/flotte", payload);
        toast.success(`${payload.marque} ${payload.modele} ajouté à la flotte`);
      }
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="flotte-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Car className="w-5 h-5 text-[#52a675]" />
            {editing ? "Modifier le véhicule" : "Ajouter un véhicule à la flotte"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Marque *</Label>
              <Input value={form.marque} onChange={(e) => setForm({ ...form, marque: e.target.value })} placeholder="Renault, VW…" data-testid="flotte-marque" />
            </div>
            <div>
              <Label>Modèle *</Label>
              <Input value={form.modele} onChange={(e) => setForm({ ...form, modele: e.target.value })} placeholder="Clio, Polo…" data-testid="flotte-modele" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Plaque *</Label>
              <Input value={form.plaque} onChange={(e) => setForm({ ...form, plaque: e.target.value.toUpperCase() })} placeholder="GE 123 456" data-testid="flotte-plaque" />
            </div>
            <div>
              <Label>Année</Label>
              <Input type="number" value={form.annee} onChange={(e) => setForm({ ...form, annee: e.target.value })} placeholder="2022" data-testid="flotte-annee" />
            </div>
            <div>
              <Label>Couleur</Label>
              <Input value={form.couleur} onChange={(e) => setForm({ ...form, couleur: e.target.value })} placeholder="Blanc" data-testid="flotte-couleur" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>VIN</Label>
              <Input value={form.vin} onChange={(e) => setForm({ ...form, vin: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "") })} maxLength={17} className="font-mono" data-testid="flotte-vin" />
            </div>
            <div>
              <Label>Carburant</Label>
              <Select value={form.carburant || ""} onValueChange={(v) => setForm({ ...form, carburant: v })}>
                <SelectTrigger data-testid="flotte-carburant"><SelectValue placeholder="Sélectionner…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="essence">Essence</SelectItem>
                  <SelectItem value="diesel">Diesel</SelectItem>
                  <SelectItem value="hybride">Hybride</SelectItem>
                  <SelectItem value="electrique">Électrique</SelectItem>
                  <SelectItem value="autre">Autre</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Km de référence</Label>
              <Input type="number" value={form.km_reference} onChange={(e) => setForm({ ...form, km_reference: e.target.value })} data-testid="flotte-km" />
            </div>
            <div>
              <Label>Tarif par jour (CHF)</Label>
              <Input type="number" step="1" value={form.tarif_jour} onChange={(e) => setForm({ ...form, tarif_jour: e.target.value })} placeholder="45" data-testid="flotte-tarif" />
            </div>
          </div>
          <div>
            <Label>Notes</Label>
            <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Équipements, particularités…" data-testid="flotte-notes" />
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.actif} onChange={(e) => setForm({ ...form, actif: e.target.checked })} className="w-4 h-4 accent-[#52a675]" data-testid="flotte-actif" />
            <span className="text-sm text-gray-700">Véhicule actif dans la flotte</span>
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={submit} disabled={submitting} className="bg-[#52a675] hover:bg-[#3d8a5c]" data-testid="flotte-save">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (editing ? "Mettre à jour" : "Ajouter à la flotte")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function FlotteTab() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const canEdit = user?.role === "admin" || user?.role === "comptoir";
  const canDelete = user?.role === "admin";

  const fetchAll = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/flotte");
      setItems(data);
    } catch (e) {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const toggleMaintenance = async (it) => {
    const target = it.statut === "maintenance" ? "disponible" : "maintenance";
    if (it.statut === "en_pret") {
      toast.error("Impossible : un prêt actif est en cours");
      return;
    }
    try {
      await api.patch(`/flotte/${it.id}/statut`, { statut: target });
      toast.success(`Statut → ${STATUT_LABELS[target]}`);
      fetchAll();
    } catch (e) { toast.error("Erreur"); }
  };

  const removeOne = async (it) => {
    if (!window.confirm(`Supprimer ${it.marque} ${it.modele} (${it.plaque}) de la flotte ?`)) return;
    try {
      await api.delete(`/flotte/${it.id}`);
      toast.success("Supprimé");
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  return (
    <div data-testid="flotte-tab">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-heading font-bold text-gray-900 flex items-center gap-2">
            <Car className="w-5 h-5 text-[#52a675]" /> Flotte de véhicules de remplacement
          </h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Véhicules disponibles pour prêt courtoisie ou location aux clients.
          </p>
        </div>
        {canEdit && (
          <Button onClick={() => { setEditing(null); setOpen(true); }} className="bg-[#52a675] hover:bg-[#3d8a5c]" data-testid="flotte-add">
            <Plus className="w-4 h-4 mr-2" /> Ajouter
          </Button>
        )}
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
      ) : items.length === 0 ? (
        <Card className="p-10 text-center border-dashed border-2 border-gray-200">
          <Car className="w-10 h-10 mx-auto text-gray-300 mb-2" />
          <p className="text-sm text-gray-500 mb-3">Aucun véhicule dans la flotte pour le moment.</p>
          {canEdit && (
            <Button onClick={() => { setEditing(null); setOpen(true); }} className="bg-[#52a675] hover:bg-[#3d8a5c]">
              <Plus className="w-4 h-4 mr-2" /> Ajouter le premier véhicule
            </Button>
          )}
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map((v) => (
            <Card key={v.id} className="p-4 border-gray-200" data-testid={`flotte-card-${v.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div>
                  <div className="font-bold text-gray-900">{v.marque} {v.modele}</div>
                  <code className="text-xs font-mono text-gray-600 bg-gray-50 px-1.5 py-0.5 rounded mt-0.5 inline-block">{v.plaque}</code>
                </div>
                <Badge variant="outline" className={STATUT_COLORS[v.statut] || "bg-gray-50"}>
                  {STATUT_LABELS[v.statut]}
                </Badge>
              </div>
              <div className="text-xs text-gray-500 space-y-0.5">
                {v.annee && <div>Année : {v.annee}{v.couleur && ` · ${v.couleur}`}</div>}
                <div>Tarif : <span className="font-semibold text-gray-700">{(v.tarif_jour || 0).toFixed(0)} CHF/j</span></div>
                {v.km_reference !== undefined && <div>Km réf. : {v.km_reference.toLocaleString("fr-CH")}</div>}
                {!v.actif && <div className="text-orange-700 font-semibold">⚠ Inactif</div>}
              </div>
              {canEdit && (
                <div className="flex gap-1 mt-3 pt-3 border-t border-gray-100">
                  <Button size="sm" variant="outline" onClick={() => { setEditing(v); setOpen(true); }} className="flex-1" data-testid={`flotte-edit-${v.id}`}>
                    <Edit className="w-3.5 h-3.5 mr-1" /> Modifier
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => toggleMaintenance(v)} disabled={v.statut === "en_pret"} data-testid={`flotte-maint-${v.id}`}>
                    {v.statut === "maintenance" ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Wrench className="w-3.5 h-3.5" />}
                  </Button>
                  {canDelete && (
                    <Button size="sm" variant="outline" onClick={() => removeOne(v)} className="text-red-600 hover:bg-red-50" data-testid={`flotte-delete-${v.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      <FlotteDialog open={open} onClose={() => setOpen(false)} onSaved={fetchAll} editing={editing} />
    </div>
  );
}
